import React, { useEffect, useMemo, useState } from "react";
import { api, STATUS_LABELS, TIPOS_PREDEFINIDOS } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Plus, ChevronLeft, ChevronRight, CalendarDays, Bike, Users, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import ServicoFormDialog from "../components/ServicoFormDialog";

const WEEKDAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const MONTH_NAMES = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

function toIsoDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function buildMonthMatrix(year, month) {
    const first = new Date(year, month, 1);
    // Monday=0..Sunday=6
    const offset = (first.getDay() + 6) % 7;
    const start = new Date(year, month, 1 - offset);
    const days = [];
    for (let i = 0; i < 42; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        days.push(d);
    }
    return days;
}

export default function DashboardPage() {
    const [stats, setStats] = useState(null);
    const [servicos, setServicos] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [motos, setMotos] = useState([]);
    const today = useMemo(() => new Date(), []);
    const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
    const [selected, setSelected] = useState(toIsoDate(today));
    const [openCreate, setOpenCreate] = useState(false);

    async function loadAll() {
        const [s, sv, cl, mt] = await Promise.all([
            api.get("/dashboard/stats"),
            api.get("/servicos"),
            api.get("/clientes"),
            api.get("/motos"),
        ]);
        setStats(s.data);
        setServicos(sv.data);
        setClientes(cl.data);
        setMotos(mt.data);
    }

    useEffect(() => {
        loadAll();
    }, []);

    const servicosPorData = useMemo(() => {
        const m = {};
        for (const s of servicos) {
            if (!m[s.data]) m[s.data] = [];
            m[s.data].push(s);
        }
        return m;
    }, [servicos]);

    const motosOnSite = useMemo(() => {
        const ids = new Set(
            servicos.filter((s) => s.status === "em_curso" || s.status === "pendente").map((s) => s.moto_id)
        );
        return motos.filter((m) => ids.has(m.id));
    }, [servicos, motos]);

    const monthDays = useMemo(
        () => buildMonthMatrix(cursor.getFullYear(), cursor.getMonth()),
        [cursor]
    );

    const selectedServicos = servicosPorData[selected] || [];
    const clienteById = useMemo(() => Object.fromEntries(clientes.map((c) => [c.id, c])), [clientes]);
    const motoById = useMemo(() => Object.fromEntries(motos.map((m) => [m.id, m])), [motos]);

    return (
        <>
            <PageHeader
                eyebrow="Painel"
                title="Dashboard"
                description="Vista geral da agenda e do estado dos serviços da oficina."
                testid="dashboard-header"
                actions={
                    <Button
                        onClick={() => setOpenCreate(true)}
                        data-testid="new-servico-btn"
                        className="rounded-sm bg-[#FF3B30] hover:bg-[#D63026]"
                    >
                        <Plus className="mr-2 h-4 w-4" /> Nova marcação
                    </Button>
                }
            />

            {/* Stats */}
            <section className="mb-8 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="dashboard-stats">
                <StatCard icon={CalendarDays} label="Serviços hoje" value={stats?.servicos_hoje ?? "—"} testid="stat-hoje" />
                <StatCard icon={Wrench} label="Em curso" value={stats?.servicos_em_curso ?? "—"} testid="stat-em-curso" />
                <StatCard icon={Users} label="Clientes" value={stats?.total_clientes ?? "—"} testid="stat-clientes" />
                <StatCard icon={Bike} label="Motos" value={stats?.total_motos ?? "—"} testid="stat-motos" />
            </section>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* Calendar */}
                <section className="lg:col-span-2 border border-zinc-200 bg-white" data-testid="calendar-section">
                    <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
                        <div>
                            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                                Agenda
                            </div>
                            <div className="font-['Outfit'] text-lg font-semibold text-zinc-900">
                                {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <Button
                                size="icon"
                                variant="outline"
                                className="h-8 w-8 rounded-sm"
                                onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                                data-testid="calendar-prev-btn"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                className="h-8 rounded-sm"
                                onClick={() => {
                                    const t = new Date();
                                    setCursor(new Date(t.getFullYear(), t.getMonth(), 1));
                                    setSelected(toIsoDate(t));
                                }}
                                data-testid="calendar-today-btn"
                            >
                                Hoje
                            </Button>
                            <Button
                                size="icon"
                                variant="outline"
                                className="h-8 w-8 rounded-sm"
                                onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                                data-testid="calendar-next-btn"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>

                    <div className="grid grid-cols-7 border-b border-zinc-200 bg-zinc-50">
                        {WEEKDAYS.map((w) => (
                            <div
                                key={w}
                                className="py-2 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500"
                            >
                                {w}
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-7">
                        {monthDays.map((d) => {
                            const iso = toIsoDate(d);
                            const inMonth = d.getMonth() === cursor.getMonth();
                            const isToday = iso === toIsoDate(today);
                            const isSelected = iso === selected;
                            const dayServicos = servicosPorData[iso] || [];
                            return (
                                <button
                                    key={iso}
                                    onClick={() => setSelected(iso)}
                                    data-testid={`calendar-day-${iso}`}
                                    className={`relative flex min-h-[90px] flex-col items-start gap-1 border-b border-r border-zinc-200 p-2 text-left transition-colors hover:bg-zinc-50 ${
                                        !inMonth ? "bg-zinc-50 text-zinc-400" : "bg-white"
                                    } ${isSelected ? "ring-2 ring-inset ring-[#FF3B30]" : ""}`}
                                >
                                    <span
                                        className={`text-xs font-semibold ${
                                            isToday ? "rounded-sm bg-[#FF3B30] px-1.5 py-0.5 text-white" : "text-zinc-900"
                                        } ${!inMonth ? "text-zinc-400" : ""}`}
                                    >
                                        {d.getDate()}
                                    </span>
                                    <div className="flex w-full flex-col gap-1 overflow-hidden">
                                        {dayServicos.slice(0, 2).map((s) => (
                                            <div
                                                key={s.id}
                                                className="truncate rounded-sm border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10px] text-zinc-700"
                                            >
                                                {s.hora} · {s.tipo}
                                            </div>
                                        ))}
                                        {dayServicos.length > 2 ? (
                                            <div className="text-[10px] font-semibold text-zinc-500">
                                                +{dayServicos.length - 2}
                                            </div>
                                        ) : null}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </section>

                {/* Selected day list + motos in shop */}
                <section className="space-y-6">
                    <div className="border border-zinc-200 bg-white" data-testid="selected-day-list">
                        <div className="border-b border-zinc-200 px-5 py-4">
                            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                                Marcações
                            </div>
                            <div className="font-['Outfit'] text-base font-semibold text-zinc-900">
                                {selected}
                            </div>
                        </div>
                        <div className="divide-y divide-zinc-100">
                            {selectedServicos.length === 0 ? (
                                <div className="px-5 py-8 text-center text-sm text-zinc-500">
                                    Sem marcações neste dia.
                                </div>
                            ) : (
                                selectedServicos.map((s) => (
                                    <div key={s.id} className="px-5 py-4" data-testid={`day-servico-${s.id}`}>
                                        <div className="flex items-center justify-between">
                                            <div className="font-mono text-xs text-zinc-500">{s.hora}</div>
                                            <StatusBadge status={s.status} />
                                        </div>
                                        <div className="mt-1 font-medium text-zinc-900">{s.tipo}</div>
                                        <div className="text-xs text-zinc-600">
                                            {motoById[s.moto_id]?.marca} {motoById[s.moto_id]?.modelo} ·{" "}
                                            {clienteById[s.cliente_id]?.nome}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    <div className="border border-zinc-200 bg-white" data-testid="motos-onsite-section">
                        <div className="border-b border-zinc-200 px-5 py-4">
                            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                                Na oficina
                            </div>
                            <div className="font-['Outfit'] text-base font-semibold text-zinc-900">
                                Motos atualmente
                            </div>
                        </div>
                        <div className="divide-y divide-zinc-100">
                            {motosOnSite.length === 0 ? (
                                <div className="px-5 py-8 text-center text-sm text-zinc-500">
                                    Nenhuma moto em curso.
                                </div>
                            ) : (
                                motosOnSite.map((m) => (
                                    <Link
                                        key={m.id}
                                        to={`/motos`}
                                        className="flex items-center justify-between px-5 py-3 hover:bg-zinc-50"
                                        data-testid={`onsite-moto-${m.id}`}
                                    >
                                        <div>
                                            <div className="font-medium text-zinc-900">
                                                {m.marca} {m.modelo}
                                            </div>
                                            <div className="text-xs text-zinc-500">
                                                {clienteById[m.cliente_id]?.nome || "—"}
                                            </div>
                                        </div>
                                        <Bike className="h-4 w-4 text-zinc-400" />
                                    </Link>
                                ))
                            )}
                        </div>
                    </div>
                </section>
            </div>

            <ServicoFormDialog
                open={openCreate}
                onClose={() => setOpenCreate(false)}
                onSaved={() => {
                    setOpenCreate(false);
                    loadAll();
                }}
                clientes={clientes}
                motos={motos}
                defaultDate={selected}
            />
        </>
    );
}

function StatCard({ icon: Icon, label, value, testid }) {
    return (
        <div className="border border-zinc-200 bg-white p-5" data-testid={testid}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                    {label}
                </div>
                <Icon className="h-4 w-4 text-zinc-400" strokeWidth={1.75} />
            </div>
            <div className="mt-3 font-['Outfit'] text-3xl font-semibold text-zinc-900">{value}</div>
        </div>
    );
}
